// ReadinessSpec — the readiness card's requirements as DATA, not a hard-coded
// 8-tuple. v1 scope: the spec drives the input allowlist, the `allGreen`
// predicate, the prompt's field-list, and the client's field order. It does NOT
// yet drive apply-mapping, elicitation policy, or widget send-text — those four
// layers stay hand-coded.
//
// The default spec is built FROM the existing `SLOT_FIELDS` set so the
// compile-time `SlotField` union the codebase imports stays the type source
// while the spec becomes the runtime driver. They are the same set in v1; the
// per-project future (`.loom.yml readiness_spec`) is what makes field names
// open.
//
// `SlotField` is imported TYPE-ONLY so there is no runtime cycle with
// `index.ts` (which re-exports this module at its bottom). The default-field
// table is iterated by ITS OWN keys at runtime, never by `SLOT_FIELDS`, so no
// value reaches across the cycle.

import { z } from "zod";

import type { SlotField } from "./index.js"; // type-only — no runtime cycle

/**
 * How a field's value folds into the DoR/INVEST narrative artifact. `"none"` =
 * metadata (milestone / priority / story_points) that does not enter it.
 */
export const NARRATIVE_ROLES = [
  "role",
  "means",
  "ends",
  "acceptance_criteria",
  "dod",
  "none",
] as const;
export type NarrativeRole = (typeof NARRATIVE_ROLES)[number];

/** The DoR narrative roles — the fields whose values compose the INVEST/DoR
 *  story artifact (As a {role}, I want {means}, so that {ends} + AC). */
const DOR_NARRATIVE_ROLES: ReadonlyArray<NarrativeRole> = [
  "role",
  "means",
  "ends",
  "acceptance_criteria",
];

export const readinessFieldSpecSchema = z
  .object({
    name: z.string().min(1),
    label: z.string().min(1),
    required: z.boolean(),
    order: z.number().int().nonnegative(),
    narrative: z.enum(NARRATIVE_ROLES),
  })
  .strict();
export type ReadinessFieldSpec = z.infer<typeof readinessFieldSpecSchema>;

export const readinessSpecSchema = z
  .object({
    id: z.string().min(1),
    version: z.number().int().nonnegative(),
    fields: z.array(readinessFieldSpecSchema).min(1),
  })
  .strict();
export type ReadinessSpec = z.infer<typeof readinessSpecSchema>;

// ---------------------------------------------------------------------------
// The default spec, built from a `Record<SlotField, FieldMeta>` table. TS
// forces all 8 keys, so a typo or a missing field is a compile error. Labels
// mirror `apps/web/src/focus/copy.ts` so phase-8 can switch the prompt/client
// to read the spec without copy drift.
// ---------------------------------------------------------------------------

type FieldMeta = Omit<ReadinessFieldSpec, "name">;

const DEFAULT_FIELD_META: Record<SlotField, FieldMeta> = {
  role: { label: "User", order: 0, narrative: "role", required: true },
  means: { label: "Capability", order: 1, narrative: "means", required: true },
  ends: { label: "Outcome", order: 2, narrative: "ends", required: true },
  acceptance_criteria: {
    label: "Acceptance criteria",
    order: 3,
    narrative: "acceptance_criteria",
    required: true,
  },
  dod_criteria: {
    label: "Definition of Done",
    order: 4,
    narrative: "dod",
    required: true,
  },
  // Story points is ADVISORY, not gate-blocking, and that is a correctness
  // requirement rather than a preference. The elicitation policy hides this
  // field from the user on purpose (`FIELD_POLICY.story_points = "skip"`) and
  // the PM directive forbids the agent from asking about it — so nobody is
  // allowed to answer it. A field nobody may be asked about cannot also be
  // allowed to block the apply: when the estimator produced a value the shape
  // gate refuses (the word "Small"), the card sat at "7 of 7 verified", progress
  // bar full, no question, no Accept, no timeout and no re-kick — a hard stall
  // ending only at the idle-kill timer.
  //
  // Making it optional costs almost nothing, because the value's only two
  // consumers already tolerate its absence: the split policy takes it as a
  // conditional input among several (propose-size-estimate.ts spreads it only
  // when defined), and the apply writes it to GitLab's `weight`, a Premium-only
  // field that this instance silently ignores. It still greens normally when
  // the estimator returns a number — it just no longer holds the user hostage
  // when it doesn't. `readiness-hidden-fields.test.ts` keeps the pairing honest.
  story_points: {
    label: "Story points",
    order: 5,
    narrative: "none",
    required: false,
  },
  // Milestone is OPTIONAL, and it exists only for a project that has open
  // milestones. The server lists them once per session
  // (`apps/server/src/agent/project-milestones.ts`); a project with none gets a
  // spec without this field (`resolveProjectReadinessSpec`), so nobody asks.
  //
  // Where it exists, its only answers are that project's milestones, picked
  // from the list, or a Skip. Nothing typed sets it: Focus offers no text box
  // for it, the message bar under its step sends a note, and on an issue's
  // refine the gate grounds it only from a pick (`UserMessageEvent.pickedAnswers`,
  // read in `propose-card.ts`). A plain chat, which shows no picker, still takes
  // it from the user's words. Apply resolves the title against the same list
  // and, when nothing matches (renamed or deleted since the pick, or a chat
  // naming one the project lacks), leaves the issue's milestone as it is — and
  // every surface that shows the milestone beside Accept says so first.
  milestone: { label: "Milestone", order: 6, narrative: "none", required: false },
  priority: { label: "Priority", order: 7, narrative: "none", required: true },
};

export const DEFAULT_READINESS_SPEC_ID = "loom.default";
export const DEFAULT_READINESS_SPEC_VERSION = 1;

export const DEFAULT_READINESS_SPEC: ReadinessSpec = {
  id: DEFAULT_READINESS_SPEC_ID,
  version: DEFAULT_READINESS_SPEC_VERSION,
  // Iterate the table's OWN keys (insertion order == `order`), never
  // `SLOT_FIELDS`, to avoid touching the type-only import at runtime.
  fields: (Object.keys(DEFAULT_FIELD_META) as Array<keyof typeof DEFAULT_FIELD_META>).map(
    (name) => ({ name, ...DEFAULT_FIELD_META[name] }),
  ),
};

// ---------------------------------------------------------------------------
// Selectors. These replace the hard-coded constants the gate/prompt/client use.
// ---------------------------------------------------------------------------

export const specFieldNames = (s: ReadinessSpec): string[] =>
  s.fields.map((f) => f.name);

export const requiredFieldNames = (s: ReadinessSpec): string[] =>
  s.fields.filter((f) => f.required).map((f) => f.name);

export const orderedFields = (s: ReadinessSpec): ReadinessFieldSpec[] =>
  [...s.fields].sort((a, b) => a.order - b.order);

/** Replaces the hard-coded `CARD_DOR_FIELDS` in propose-card.ts. */
export const dorNarrativeFieldNames = (s: ReadinessSpec): string[] =>
  s.fields
    .filter((f) => DOR_NARRATIVE_ROLES.includes(f.narrative))
    .map((f) => f.name);

/**
 * Resolve the readiness spec for a project. Reads an optional `readiness_spec`
 * off the project's parsed `.loom.yml` (passed as `loomConfig`), `safeParse`-ing
 * it and DEFAULTING on any failure so a malformed project spec can never crash
 * the gate (the load-bearing safety property). v1 ships no project spec, so the
 * default holds; the per-project seam is wired but the apply-mapping a custom
 * spec would also need remains follow-up.
 *
 * Takes `unknown` (not `LoomConfig`) on purpose: it keeps `spec.ts` free of a
 * runtime dependency on `../config`, which imports `readinessSpecSchema` from
 * here — the one-directional edge that avoids a module cycle.
 */
export const resolveReadinessSpec = (loomConfig?: unknown): ReadinessSpec => {
  const candidate = (loomConfig as { readiness_spec?: unknown } | null | undefined)
    ?.readiness_spec;
  if (candidate === undefined) return DEFAULT_READINESS_SPEC;
  const parsed = readinessSpecSchema.safeParse(candidate);
  if (!parsed.success) return DEFAULT_READINESS_SPEC;
  // A required field the gate cannot receive is an unescapable stall:
  // propose_card's slot `field` is `z.enum(SLOT_FIELDS)`, so no slot for a custom
  // name can ever be parsed, while computeAllGreenFields still demands it —
  // allGreen never turns true, no acceptToken is ever minted, and the flow sits on
  // the grind screen with no question and no Accept until the idle-kill timer.
  // Default out, exactly as for a malformed spec. An OPTIONAL custom field is
  // still fine (it can be asked about and simply never greens).
  //
  // Uses DEFAULT_READINESS_SPEC's names, NOT `SLOT_FIELDS`: this file's header is
  // explicit that `SlotField` is imported type-only to avoid a runtime cycle with
  // `index.ts`, and the two sets are identical by construction.
  const proposable = new Set(DEFAULT_READINESS_SPEC.fields.map((f) => f.name));
  if (!requiredFieldNames(parsed.data).every((n) => proposable.has(n))) {
    return DEFAULT_READINESS_SPEC;
  }
  return parsed.data;
};

// ---------------------------------------------------------------------------
// The milestone field, per project.
// ---------------------------------------------------------------------------

/** The readiness field a project's milestone list feeds. */
export const MILESTONE_FIELD = "milestone";

/**
 * What the server learned about a project's OPEN milestones for one session:
 * `"none"` (the list came back empty), `"some"`, or `null` (not known — the
 * list call failed, timed out, or has not answered yet). Only `"none"` changes
 * anything; `null` keeps the configured spec, exactly as before.
 */
export type ProjectMilestones = "none" | "some" | null;

/**
 * The readiness spec for one session: the project's configured spec, minus the
 * milestone field when the project has no open milestones. A milestone nobody
 * can pick is not a question worth asking.
 *
 * `id` and `version` are kept, so a card minted under the narrowed spec and
 * applied under it (or the other way round) does not read as spec drift; the
 * acceptToken hashes the green fields, not the spec. Returns the resolved spec
 * object itself when nothing is removed.
 */
export const resolveProjectReadinessSpec = (
  loomConfig: unknown,
  project: { readonly milestones: ProjectMilestones },
): ReadinessSpec => {
  const spec = resolveReadinessSpec(loomConfig);
  if (project.milestones !== "none") return spec;
  if (!spec.fields.some((f) => f.name === MILESTONE_FIELD)) return spec;
  return {
    ...spec,
    fields: spec.fields.filter((f) => f.name !== MILESTONE_FIELD),
  };
};

/** Does the spec carry a milestone field at all? */
export const specHasMilestone = (spec: ReadinessSpec): boolean =>
  spec.fields.some((f) => f.name === MILESTONE_FIELD);

const MILESTONE_LABEL_RE = /^\s*milestone\s*:\s*/iu;

/** "Milestone: 7.1 - Freigabe" → "7.1 - Freigabe" (the bare title to match).
 *  A value without the label is taken as the title itself. */
export const milestoneTitleOf = (value: string | undefined | null): string | null => {
  if (!value) return null;
  const stripped = value.replace(MILESTONE_LABEL_RE, "").trim();
  return stripped.length > 0 ? stripped : null;
};

/** Whether a card value names the milestone called `title` — trimmed and
 *  exact, the same comparison apply uses to resolve the id. */
export const milestoneTitleMatches = (
  title: string,
  value: string | undefined | null,
): boolean => {
  const wanted = milestoneTitleOf(value);
  return wanted !== null && title.trim() === wanted;
};

/**
 * The title a milestone PICK carries, or null. A pick is what the milestone
 * picker sends: exactly one line, `Milestone: <title>` (the label is English on
 * every locale, like "Priority: High"). Anything else — prose, several lines, a
 * bare title — is not a pick.
 */
export const milestonePickOf = (text: string): string | null => {
  const trimmed = text.trim();
  if (trimmed.length === 0 || trimmed.includes("\n")) return null;
  if (!MILESTONE_LABEL_RE.test(trimmed)) return null;
  return milestoneTitleOf(trimmed);
};
